import { useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import { WifiOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { BookingsErrorKind } from '../../../types/provider';

const { Text } = Typography;

interface Props {
  kind: BookingsErrorKind;
  message: string | null;
  onRetry: () => void;
}

export default function BookingErrorState({ kind, message, onRetry }: Props) {
  const { t } = useTranslation('provider');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    if (kind !== 'offline') return;

    const handleOnline = () => {
      setIsOnline(true);
      onRetry();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [kind, onRetry]);

  const visual =
    kind === 'offline'
      ? { icon: <WifiOutlined />, color: '#8c8c8c' }
      : kind === 'conflict'
        ? { icon: <ReloadOutlined />, color: '#fa8c16' }
        : { icon: <WarningOutlined />, color: '#faad14' };

  const title =
    kind === 'offline'
      ? t('bookings.errorState.offlineTitle')
      : kind === 'conflict'
        ? t('bookings.errorState.conflictTitle')
        : t('bookings.errorState.genericTitle');

  const hint =
    kind === 'offline' && !isOnline
      ? t('bookings.errorState.offlineHint')
      : message || t('bookings.errorState.defaultHint');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 32px 32px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${visual.color}14 0%, ${visual.color}24 100%)`,
          border: `1px solid ${visual.color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 30,
          color: visual.color,
          marginBottom: 16,
          boxShadow: `0 8px 24px ${visual.color}10`,
        }}
      >
        {visual.icon}
      </div>

      <Text
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--bl-text-strong, #0f172a)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {title}
      </Text>
      <Text
        style={{
          fontSize: 13,
          color: 'var(--bl-text-muted, #6b7280)',
          display: 'block',
          maxWidth: 280,
          lineHeight: 1.45,
          marginBottom: 16,
        }}
      >
        {hint}
      </Text>

      {kind !== 'conflict' && (
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={onRetry}
          style={{ borderRadius: 10, fontWeight: 600 }}
        >
          {t('bookings.errorState.retryBtn')}
        </Button>
      )}
    </div>
  );
}
